import { useState, useEffect, useMemo, useCallback } from 'react';
import { Briefcase, FileText, BarChart2, MapPin, Search, MessageSquare, DollarSign, Tag, ChevronLeft, ChevronRight, X, History, Clock, Building2, Sparkles, TrendingUp, Users, Calendar, ArrowRight, ExternalLink } from 'lucide-react';
import { apiCall } from './utils/api';
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// --- SHARED COMPONENTS (Could be moved to their own file) ---
const Input = ({ label, ...props }) => (
    <div className="space-y-2 w-full">
        {label && <label className="text-sm font-semibold text-gray-700 tracking-wide">{label}</label>}
        <input {...props} className="w-full px-5 py-3.5 bg-white/80 backdrop-blur border border-gray-200 text-gray-900 rounded-xl focus:ring-2 focus:ring-emerald-400 focus:border-transparent transition-all duration-200 placeholder:text-gray-400 shadow-sm hover:shadow-md" />
    </div>
);

// Helper to convert resume data object to a string, used by multiple components
const formatResumeDataAsText = (data) => {
    if (!data || !data.personalInfo) return "";
    let text = `Name: ${data.personalInfo.name}\nEmail: ${data.personalInfo.email}\nPhone: ${data.personalInfo.phone}\nLocation: ${data.personalInfo.location}\n\n`;
    if (data.goal) text += `## Goal\n${data.goal}\n\n`;
    text += "## Work Experience\n";
    (data.experience || []).forEach(exp => {
        text += `- ${exp.title} at ${exp.company} (${exp.period})\n  ${exp.description}\n`;
    });
    text += "\n## Education\n";
    (data.education || []).forEach(edu => {
        text += `- ${edu.degree} from ${edu.school} (${edu.period})\n  ${edu.description}\n`;
    });
    text += "\n## Projects\n";
    (data.projects || []).forEach(proj => {
        text += `- ${proj.title} (${proj.period})\n  ${proj.description}\n`;
    });
    text += "\n## Skills\n";
    {
        const skills = Array.isArray(data.skills)
            ? data.skills.filter(Boolean).map(String)
            : (typeof data.skills === 'string'
                ? data.skills.split(',').map(s => s.trim()).filter(Boolean)
                : []);
        text += skills.join(', ');
    }
    text += "\n\n## Achievements\n";
    (data.achievements || []).forEach(ach => {
        text += `- ${ach.description}\n`;
    });
    text += "\n## Awards\n";
    (data.awards || []).forEach(award => {
        text += `- ${award.description}\n`;
    });
    text += "\n## Certificates\n";
    (data.certificates || []).forEach(cert => {
        text += `- ${cert.description}\n`;
    });
    text += "\n## Hobbies\n";
    (data.hobbies || []).forEach(hobby => {
        text += `- ${hobby.description}\n`;
    });
    return text;
};


// --- NEW CACHING HOOK ---
const useJobCache = (cacheKey) => {
    const [cachedData, setCachedData] = useState(() => {
        try {
            const item = window.sessionStorage.getItem(cacheKey);
            return item ? JSON.parse(item) : null;
        } catch (error) {
            console.error("Error reading from sessionStorage", error);
            return null;
        }
    });

    const setData = useCallback((newData) => {
        try {
            const valueToStore = JSON.stringify(newData);
            window.sessionStorage.setItem(cacheKey, valueToStore);
            setCachedData(newData);
        } catch (error) {
            console.error("Error writing to sessionStorage", error);
        }
    }, [cacheKey]);

    useEffect(() => {
        try {
            const item = window.sessionStorage.getItem(cacheKey);
            setCachedData(item ? JSON.parse(item) : null);
        } catch (error) {
            console.error("Error reading from sessionStorage", error);
            setCachedData(null);
        }
    }, [cacheKey]);

    return [cachedData, setData];
};


const JobDashboard = ({ resumeData, onGenerateCoverLetter, onGenerateInterviewQuestions, setActiveTab }) => {

    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState({ what: resumeData?.experience?.[0]?.title || '', where: '' });
    const [selectedJob, setSelectedJob] = useState(null);
    const [isDetailLoading, setIsDetailLoading] = useState(false);
    const [selectedCountry, setSelectedCountry] = useState('gb');
    const [currentPage, setCurrentPage] = useState(1);
    const [searchHistory, setSearchHistory] = useState([]);

    useEffect(() => {
        const history = JSON.parse(localStorage.getItem('jobSearchHistory')) || [];
        setSearchHistory(history);
    }, []);

    const cacheKey = useMemo(() => {


        if (!searchQuery.what) return `jobs:match:${selectedCountry}:${currentPage}`;

        const what = searchQuery.what.trim().toLowerCase();
        const where = searchQuery.where.trim().toLowerCase();


        if (what || where) {
            return `jobs:search:${selectedCountry}:${what}:${where}:${currentPage}`;
        }
        return `jobs:match:${selectedCountry}:${currentPage}`;
    }, [searchQuery, selectedCountry, currentPage]);

    const [jobData, setJobData] = useJobCache(cacheKey);

    const jobs = jobData?.results || [];
    const totalJobs = jobData?.count || 0;

    const totalPages = Math.min(10, Math.ceil(totalJobs / 15));

    const fetchJobs = useCallback(async (url, body) => {
        setIsLoading(true);
        setError(null);
        try {
            const { data } = await apiCall(url, {
                method: 'POST',
                body: body,
            });
            setJobData(data);
            if (data.results && data.results.length > 0) {
                if (currentPage === 1) {
                    setSelectedJob(data.results[0]);
                }
            } else {
                setSelectedJob(null);
            }
        } catch (err) {
            setError(err.message);
            setJobData(null);
        } finally {
            setIsLoading(false);
        }
    }, [setJobData, currentPage]);

    const handleSearch = useCallback((e) => {
        if (e) e.preventDefault();

        const newSearch = { what: searchQuery.what, where: searchQuery.where, country: selectedCountry };
        if (newSearch.what || newSearch.where) {
            const history = [newSearch, ...searchHistory.filter(s => s.what !== newSearch.what || s.where !== newSearch.where)].slice(0, 5);
            setSearchHistory(history);
            localStorage.setItem('jobSearchHistory', JSON.stringify(history));
        }

        setCurrentPage(1);
    }, [searchQuery, selectedCountry, searchHistory]);

    const handleClearSearch = () => {
        setSearchQuery({ what: '', where: '' });
        setCurrentPage(1);
    };

    const handlePageChange = (newPage) => {
        if (newPage > 0 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };

    const handleSelectJob = (job) => {
        setSelectedJob(job);
    };



    useEffect(() => {
        const formData = new FormData();
        formData.append('country', selectedCountry);
        formData.append('page', currentPage);

        if (searchQuery.what || searchQuery.where) {
            formData.append('what', searchQuery.what);
            formData.append('where', searchQuery.where);
            fetchJobs(`${API_BASE_URL}/api/jobs/search`, formData);
        } else if (resumeData) {
            const resumeText = formatResumeDataAsText(resumeData);
            formData.append('resume_text', resumeText);
            fetchJobs(`${API_BASE_URL}/api/jobs/match`, formData);
        } else {
            fetchJobs(`${API_BASE_URL}/api/jobs/search`, formData);
        }
    }, [currentPage, searchQuery, selectedCountry, resumeData, fetchJobs]);

    useEffect(() => {
        setSearchQuery(prev => ({ ...prev, where: '' }));
    }, [selectedCountry]);


    return (
        <div className="flex flex-col bg-gradient-to-br from-gray-50 via-white to-emerald-50/30 rounded-2xl p-6">
            <div className="flex-grow pr-0">
                {isLoading && (
                    <div className="text-center py-16">
                        <div className="relative inline-flex">
                            <div className="w-16 h-16 bg-emerald-200 rounded-full animate-ping absolute"></div>
                            <div className="w-16 h-16 bg-emerald-300 rounded-full animate-ping absolute animation-delay-200"></div>
                            <svg className="animate-spin h-16 w-16 text-emerald-600 relative z-10" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        </div>
                        <p className="mt-4 text-gray-600 font-medium animate-pulse">Finding perfect matches for you...</p>
                    </div>
                )}



                {jobs && jobs.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 auto-rows-max">
                        {jobs.map((job, index) => (
                            <div
                                key={job.id}
                                className="transform transition-all duration-300 hover:scale-[1.02] animate-fadeIn"
                                style={{ animationDelay: `${index * 50}ms` }}
                            >
                                <JobCard
                                    job={job}
                                    onSelectJob={handleSelectJob}
                                    isSelected={selectedJob?.id === job.id}
                                />
                            </div>
                        ))}
                    </div>
                ) : (
                    !isLoading && (
                        <div className="text-center py-20">
                            <div className="inline-flex items-center justify-center w-20 h-20 bg-gray-100 rounded-full mb-4">
                                <Briefcase className="w-10 h-10 text-gray-400" />
                            </div>
                            <p className="text-gray-500 text-lg font-medium">No jobs found</p>
                            <p className="text-gray-400 mt-2">Try adjusting your search criteria</p>
                        </div>
                    )
                )}
            </div>


        </div>

    );
};

const JobCard = ({ job, onSelectJob, isSelected, interactive = true }) => {
    const summary = job.summary || "No summary available.";

    const formatSalary = (min, max, currencySymbol = '$') => {
        if (!min && !max) return "Not disclosed";
        const format = (val) => `${currencySymbol}${Math.round(val / 1000)}k`;
        if (min && max) {
            if (min === max) return format(min);
            return `${format(min)} - ${format(max)}`;
        }
        if (min) return `From ${format(min)}`;
        if (max) return `Up to ${format(max)}`;
        return "Not disclosed";
    };

    const currencyMap = {
        'gb': '£', 'us': '$', 'ca': '$', 'au': '$', 'nz': '$', 'sg': '$',
        'de': '€', 'fr': '€', 'it': '€', 'es': '€', 'nl': '€', 'be': '€', 'at': '€',
        'in': '₹', 'br': 'R$', 'mx': '$', 'za': 'R', 'pl': 'zł', 'ch': 'CHF'
    };

    const currencySymbol = currencyMap[job.location?.country] || '$';
    const salary = formatSalary(job.salary_min, job.salary_max, currencySymbol);

    const Container = interactive ? 'button' : 'div';

    return (
        <Container
            onClick={() => interactive && onSelectJob && onSelectJob(job)}
            className={`group w-full text-left p-6 rounded-2xl border-2 transition-all duration-300 space-y-4 relative overflow-hidden ${isSelected
                ? 'bg-gradient-to-br from-emerald-50 via-white to-teal-50 border-emerald-400 shadow-xl shadow-emerald-100/50'
                : 'bg-white border-gray-200 hover:border-emerald-300 hover:shadow-xl hover:shadow-gray-200/50'
                }`}
        >
            {/* Decorative gradient overlay */}
            <div className={`absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-emerald-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none`}></div>

            {/* Selected indicator */}


            <div className="relative z-10">
                <div className="flex justify-between items-start gap-3">
                    <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-bold text-gray-900 truncate group-hover:text-emerald-700 transition-colors">
                            {job.title}
                        </h3>
                        <div className="flex items-center gap-2 mt-1">
                            <Building2 size={14} className="text-gray-400 flex-shrink-0" />
                            <p className="text-gray-700 font-semibold truncate">{job.company?.display_name}</p>
                        </div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                        {job.category && job.category.label && (
                            <span className="flex-shrink-0 bg-gradient-to-r from-emerald-50 to-teal-50 text-emerald-700 text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1.5 border border-emerald-200">
                                <Sparkles size={12} />
                                {job.category.label}
                            </span>
                        )}
                    </div>
                </div>

                <div className="text-sm text-gray-600 flex flex-wrap items-center gap-x-4 gap-y-2 mt-4">
                    <span className="flex items-center gap-1.5 bg-gray-50 px-2.5 py-1 rounded-lg">
                        <MapPin size={14} className="text-emerald-500" />
                        <span className="font-medium">{job.location?.display_name}</span>
                    </span>
                    <span className="flex items-center gap-1.5 bg-gray-50 px-2.5 py-1 rounded-lg">
                        <Clock size={14} className="text-blue-500" />
                        <span className="font-medium">{job.contract_time || 'Full-time'}</span>
                    </span>
                    <span className="flex items-center gap-1.5 bg-gradient-to-r from-green-50 to-emerald-50 px-2.5 py-1 rounded-lg border border-green-200">
                        <DollarSign size={14} className="text-green-600" />
                        <span className="font-bold text-green-700">{salary}</span>
                    </span>
                </div>

                <div className="pt-4 mt-4 border-t border-gray-100">
                    <p className="text-sm text-gray-600 line-clamp-2 leading-relaxed">{summary}</p>
                </div>

                {/* Apply button with enhanced styling */}
                <div className="mt-4 pt-4 border-t border-gray-100">
                    <a
                        href={job.redirect_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex items-center gap-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold py-2.5 px-5 rounded-xl hover:from-emerald-600 hover:to-teal-600 transform hover:scale-105 transition-all duration-200 shadow-lg shadow-emerald-200/50 hover:shadow-xl hover:shadow-emerald-300/50"
                    >
                        Apply Now
                        <ExternalLink size={16} />
                    </a>
                </div>
            </div>
        </Container>
    );
};

const Pagination = ({ currentPage, totalPages, onPageChange }) => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    const halfPagesToShow = Math.floor(maxPagesToShow / 2);

    if (totalPages <= maxPagesToShow) {
        for (let i = 1; i <= totalPages; i++) {
            pageNumbers.push(i);
        }
    } else {
        if (currentPage <= halfPagesToShow) {
            for (let i = 1; i <= maxPagesToShow - 1; i++) {
                pageNumbers.push(i);
            }
            pageNumbers.push('...');
            pageNumbers.push(totalPages);
        } else if (currentPage >= totalPages - halfPagesToShow) {
            pageNumbers.push(1);
            pageNumbers.push('...');
            for (let i = totalPages - maxPagesToShow + 2; i <= totalPages; i++) {
                pageNumbers.push(i);
            }
        } else {
            pageNumbers.push(1);
            pageNumbers.push('...');
            for (let i = currentPage - halfPagesToShow + 1; i <= currentPage + halfPagesToShow - 1; i++) {
                pageNumbers.push(i);
            }
            pageNumbers.push('...');
            pageNumbers.push(totalPages);
        }
    }

    return (
        <nav className="flex items-center justify-center space-x-2">
            <button
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="p-2.5 rounded-xl bg-white border-2 border-gray-200 hover:border-emerald-300 hover:bg-emerald-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 group"
            >
                <ChevronLeft size={20} className="text-gray-600 group-hover:text-emerald-600" />
            </button>

            <div className="flex items-center space-x-1">
                {pageNumbers.map((page, index) => (
                    page === '...' ? (
                        <span key={`ellipsis-${index}`} className="px-3 py-2 text-gray-400 font-medium">...</span>
                    ) : (
                        <button
                            key={page}
                            onClick={() => onPageChange(page)}
                            className={`min-w-[40px] h-10 px-3 rounded-xl font-semibold transition-all duration-200 ${currentPage === page
                                ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-lg shadow-emerald-200/50 scale-110'
                                : 'bg-white border-2 border-gray-200 text-gray-700 hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-700'
                                }`}
                        >
                            {page}
                        </button>
                    )
                ))}
            </div>

            <button
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="p-2.5 rounded-xl bg-white border-2 border-gray-200 hover:border-emerald-300 hover:bg-emerald-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 group"
            >
                <ChevronRight size={20} className="text-gray-600 group-hover:text-emerald-600" />
            </button>
        </nav>
    );
};

// Add custom styles for animations and scrollbar
const style = document.createElement('style');
style.textContent = `
    @keyframes fadeIn {
        from {
            opacity: 0;
            transform: translateY(10px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }
    
    .animate-fadeIn {
        animation: fadeIn 0.5s ease-out forwards;
    }
    
    .animation-delay-200 {
        animation-delay: 200ms;
    }
    
    .custom-scrollbar::-webkit-scrollbar {
        width: 8px;
    }
    
    .custom-scrollbar::-webkit-scrollbar-track {
        background: #f3f4f6;
        border-radius: 10px;
    }
    
    .custom-scrollbar::-webkit-scrollbar-thumb {
        background: linear-gradient(to bottom, #10b981, #14b8a6);
        border-radius: 10px;
    }
    
    .custom-scrollbar::-webkit-scrollbar-thumb:hover {
        background: linear-gradient(to bottom, #059669, #0d9488);
    }
    
    .line-clamp-2 {
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
    }
`;
document.head.appendChild(style);

export default JobDashboard;
